import clsx from 'clsx';
import { FileSpreadsheet, HandCoins, RotateCcw, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addMonths } from '@pz/core';
import { HoursInput } from '../components/entry';
import { signedHours } from '../components/leave';
import { Button, EmptyState, ErrorBox, Field, IconButton, Input, Loading, Modal, Notice, Pill, Select, Textarea, td, th, useToast } from '../components/ui';
import { saveFile } from '../lib/download';
import { d, h, month as monthLabel } from '../lib/format';
import { useM, useQ } from '../lib/hooks';
import { ScrollTable } from '../components/virtual';
import { useMe } from '../lib/me';
import { rpc } from '../lib/rpc';

export interface AdjustmentView {
  id: string;
  kind: 'payout' | 'correction';
  minutes: number;
  date: string;
  note: string | null;
  createdBy?: string | null;
}

/** Payouts and corrections of the extra hours (own view and HR). */
export function AdjustmentList({ items, onRemove }: { items: AdjustmentView[]; onRemove?: (a: AdjustmentView) => void }) {
  const { t } = useTranslation();
  if (!items.length) return <p className="text-xs text-graphite">{t('overtime.none')}</p>;
  return (
    <ul className="flex flex-col gap-1 text-sm">
      {items.map((a) => (
        <li key={a.id} className="flex flex-wrap items-center gap-2">
          <Pill tone={a.kind === 'payout' ? 'info' : 'neutral'}>{t(`overtime.kinds.${a.kind}`)}</Pill>
          <span className="num font-medium">{signedHours(a.minutes)} h</span>
          <span className="text-graphite">{a.kind === 'payout' ? t('overtime.payrollMonth', { month: monthLabel(a.date) }) : d(a.date)}</span>
          {a.note && <span className="text-xs text-graphite">· {a.note}</span>}
          {a.createdBy && <span className="text-xs text-faint">· {a.createdBy}</span>}
          {onRemove && <IconButton label={t('common.delete')} icon={<Trash2 className="size-3.5" />} onClick={() => onRemove(a)} />}
        </li>
      ))}
    </ul>
  );
}

/**
 * HR: extra hours per month for payroll. Where they were worked (days off, public holidays, absence days,
 * above the target on working days), what was taken as time off in lieu and what was paid out; payouts and
 * corrections are entered here and lower or raise the balance at once.
 */
export function OvertimeTab() {
  const { t } = useTranslation();
  const { me } = useMe();
  const toast = useToast();
  const months = useMemo(() => {
    const start = (me.settings.trackingStart ?? `${me.today.slice(0, 4)}-01-01`).slice(0, 7);
    const out: string[] = [];
    for (let m = `${me.today.slice(0, 7)}-01`; m.slice(0, 7) >= start && out.length < 24; m = addMonths(m, -1)) out.push(m);
    return out;
  }, [me.today, me.settings.trackingStart]);
  const [month, setMonth] = useState(months[0] ?? `${me.today.slice(0, 7)}-01`);
  const q = useQ('overtime.report', { month });
  const [person, setPerson] = useState<{ id: string; name: string } | null>(null);
  // reset of the balance of one person (request 23 of round 4)
  const [resetting, setResetting] = useState<{ id: string; name: string; balance: number | null } | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <Select className="w-44" value={month} onChange={(e) => setMonth(e.target.value)} aria-label={t('overtime.month')}>
          {months.map((m) => (
            <option key={m} value={m}>
              {monthLabel(m)}
            </option>
          ))}
        </Select>
        {q.data && (q.data.closed ? <Pill tone="good">{t('overtime.closed')}</Pill> : <Pill tone="warn">{t('overtime.open')}</Pill>)}
        <Button
          size="sm"
          icon={<FileSpreadsheet className="size-4" />}
          loading={busy}
          onClick={() => {
            setBusy(true);
            rpc('overtime.reportFile', { month })
              .then(saveFile)
              .catch(toast.error)
              .finally(() => setBusy(false));
          }}
        >
          {t('overtime.excel')}
        </Button>
      </div>
      <Notice tone="neutral">{t('overtime.intro')}</Notice>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && q.data.rows.length === 0 && <EmptyState>{t('overtime.empty')}</EmptyState>}
      {q.data && q.data.rows.length > 0 && (
        <ScrollTable label={t('overtime.tab')}>
          <table className="w-full min-w-[1100px] text-sm">
            <thead className="border-b border-line bg-sheet-2">
              <tr>
                <th className={th}>{t('common.person')}</th>
                <th className={clsx(th, 'text-right')}>{t('overtime.cols.target')}</th>
                <th className={clsx(th, 'text-right')}>{t('overtime.cols.booked')}</th>
                <th className={clsx(th, 'text-right')}>{t('overtime.cols.difference')}</th>
                <th className={clsx(th, 'text-right')} title={t('overtime.cols.daysOffHint')}>
                  {t('overtime.cols.daysOff')}
                </th>
                <th className={clsx(th, 'text-right')}>{t('overtime.cols.holiday')}</th>
                <th className={clsx(th, 'text-right')}>{t('overtime.cols.absenceDay')}</th>
                <th className={clsx(th, 'text-right')}>{t('overtime.cols.workdayExtra')}</th>
                <th className={clsx(th, 'text-right')}>{t('overtime.cols.compensation')}</th>
                <th className={clsx(th, 'text-right')}>{t('overtime.cols.paidOut')}</th>
                <th className={clsx(th, 'text-right')} title={t('overtime.cols.balanceHint')}>
                  {t('overtime.cols.balance')}
                </th>
              </tr>
            </thead>
            <tbody>
              {q.data.rows.map((r) => (
                <tr key={r.personId} className="border-b border-line/70">
                  <td className={td}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-medium">{r.name}</div>
                        <div className="text-xs text-graphite">
                          {[r.discipline, r.country].filter(Boolean).join(' · ')}
                          {r.openEntries > 0 && <span className="block text-warn">{t('overtime.openEntries', { count: r.openEntries })}</span>}
                          {r.trackingStart && <span className="block">{t('overtime.reset.since', { date: d(r.trackingStart) })}</span>}
                        </div>
                      </div>
                      <span className="flex shrink-0 gap-0.5">
                        <IconButton label={t('overtime.adjust')} icon={<HandCoins className="size-3.5" />} onClick={() => setPerson({ id: r.personId, name: r.name })} />
                        {r.overtimeAccount && (
                          <IconButton label={t('overtime.reset.button')} icon={<RotateCcw className="size-3.5" />} onClick={() => setResetting({ id: r.personId, name: r.name, balance: r.balanceMinutes })} />
                        )}
                      </span>
                    </div>
                  </td>
                  <td className={clsx(td, 'num text-right')}>{h(r.targetMinutes, 1)}</td>
                  <td className={clsx(td, 'num text-right')}>{h(r.bookedMinutes, 1)}</td>
                  <td className={clsx(td, 'num text-right font-medium', r.differenceMinutes < 0 && 'text-bad')}>{signedHours(r.differenceMinutes)}</td>
                  <td className={clsx(td, 'num text-right')}>{r.daysOffMinutes ? h(r.daysOffMinutes, 1) : ''}</td>
                  <td className={clsx(td, 'num text-right')}>{r.holidayMinutes ? h(r.holidayMinutes, 1) : ''}</td>
                  <td className={clsx(td, 'num text-right')}>{r.absenceDayMinutes ? h(r.absenceDayMinutes, 1) : ''}</td>
                  <td className={clsx(td, 'num text-right')}>{r.workdayExtraMinutes ? h(r.workdayExtraMinutes, 1) : ''}</td>
                  <td className={clsx(td, 'num text-right')}>{r.compensationMinutes ? h(r.compensationMinutes, 1) : ''}</td>
                  <td className={clsx(td, 'num text-right')}>{r.paidOutMinutes ? h(r.paidOutMinutes, 1) : ''}</td>
                  <td className={clsx(td, 'num text-right font-semibold', (r.balanceMinutes ?? 0) < 0 && 'text-bad')}>{r.balanceMinutes === null ? '-' : signedHours(r.balanceMinutes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollTable>
      )}
      <AdjustDialog person={person} defaultMonth={month} onClose={() => setPerson(null)} />
      <ResetDialog person={resetting} onClose={() => setResetting(null)} />
    </div>
  );
}

/**
 * "Saldo zurücksetzen" (request 23 of round 4): the own Erfassungsbeginn of the person becomes the chosen day (default
 * today). Extra hours, target/actual and the reminders of missing bookings count from that day on, the balance starts at
 * 0; the booked hours stay as they are. The person gets a notification with the hours removed. Undo sets the previous
 * day again.
 */
function ResetDialog({ person, onClose }: { person: { id: string; name: string; balance: number | null } | null; onClose: () => void }) {
  const { t } = useTranslation();
  const { me } = useMe();
  const toast = useToast();
  const set = useM('overtime.setStart', { invalidate: ['overtime.', 'leave.', 'kpi.', 'workload.', 'notifications.'] });
  const [date, setDate] = useState(me.today);
  const [note, setNote] = useState('');
  const close = () => {
    setDate(me.today);
    setNote('');
    onClose();
  };
  const run = async () => {
    if (!person) return;
    try {
      const r = await set.mutateAsync({ personId: person.id, date, note: note.trim() || null });
      toast.undoable(t('overtime.reset.done', { name: person.name, hours: signedHours(r.removedMinutes) }), t('common.undo'), () =>
        set.mutateAsync({ personId: person.id, date: r.previousStart, note: t('overtime.reset.undoNote') }).then(() => toast.ok(t('common.undone'))).catch(toast.error),
      );
      close();
    } catch {
      /* shown in the dialog */
    }
  };
  return (
    <Modal
      open={!!person}
      onClose={close}
      title={person ? t('overtime.reset.title', { name: person.name }) : ''}
      footer={
        <>
          <Button onClick={close}>{t('common.cancel')}</Button>
          <Button variant="danger" loading={set.isPending} disabled={!date || date > me.today} onClick={run}>
            {t('overtime.reset.confirm')}
          </Button>
        </>
      }
    >
      {person && (
        <div className="flex flex-col gap-3">
          <Notice tone="warn">
            {t('overtime.reset.question', { name: person.name, hours: person.balance === null ? '-' : signedHours(person.balance), date: d(date || me.today) })}
          </Notice>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('overtime.reset.date')} hint={t('overtime.reset.dateHint')}>
              <Input type="date" value={date} max={me.today} min={me.settings.trackingStart ?? undefined} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Field label={t('leave.note')} className="sm:col-span-2">
              <Textarea rows={2} value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder={t('overtime.reset.notePlaceholder')} />
            </Field>
          </div>
          <p className="text-xs text-graphite">{t('overtime.reset.keeps')}</p>
          <ErrorBox error={set.error} />
        </div>
      )}
    </Modal>
  );
}

/** Payout (with the salary of a month) or correction of one person's extra hours, with the list of earlier ones. */
function AdjustDialog({ person, defaultMonth, onClose }: { person: { id: string; name: string } | null; defaultMonth: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { me } = useMe();
  const toast = useToast();
  const q = useQ('overtime.person', { personId: person?.id ?? '' }, { enabled: !!person });
  const add = useM('overtime.add', { invalidate: ['overtime.', 'leave.'] });
  const remove = useM('overtime.remove', { invalidate: ['overtime.', 'leave.'] });
  const [kind, setKind] = useState<'payout' | 'correction'>('payout');
  const [minutes, setMinutes] = useState<number | null>(null);
  const [sign, setSign] = useState<1 | -1>(1);
  const [month, setMonth] = useState(defaultMonth);
  const [date, setDate] = useState(me.today);
  const [note, setNote] = useState('');
  const [removing, setRemoving] = useState<AdjustmentView | null>(null);
  const [reason, setReason] = useState('');
  const reset = () => {
    setKind('payout');
    setMinutes(null);
    setSign(1);
    setMonth(defaultMonth);
    setDate(me.today);
    setNote('');
  };
  const valid = !!minutes && minutes > 0 && (kind === 'payout' ? !!month : !!date && note.trim().length >= 3);
  const b = q.data?.balance;
  return (
    <>
      <Modal
        open={!!person && !removing}
        onClose={() => (reset(), onClose())}
        title={person ? t('overtime.adjustTitle', { name: person.name }) : ''}
        footer={
          <>
            <Button onClick={() => (reset(), onClose())}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={!valid}
              loading={add.isPending}
              onClick={() =>
                add
                  .mutateAsync({
                    personId: person!.id,
                    kind,
                    minutes: kind === 'payout' ? minutes! : sign * minutes!,
                    month: kind === 'payout' ? month : null,
                    date: kind === 'correction' ? date : null,
                    note: note.trim() || null,
                  })
                  .then(() => (toast.ok(t('common.saved')), reset()))
                  .catch(toast.error)
              }
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {b && (
            <p className="text-sm">
              {t('overtime.balanceNow')}: <span className={clsx('num font-semibold', b.availableMinutes < 0 && 'text-bad')}>{signedHours(b.availableMinutes)} h</span>
              <span className="text-graphite"> ({t('overtime.balanceParts', { earned: signedHours(b.earnedMinutes), adjusted: signedHours(b.adjustedMinutes), planned: h(b.plannedMinutes) })})</span>
              <span className="block text-xs text-graphite">{t('overtime.reset.countsFrom', { date: d(b.since) })}</span>
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('overtime.kind')}>
              <Select value={kind} onChange={(e) => setKind(e.target.value as 'payout')}>
                <option value="payout">{t('overtime.kinds.payout')}</option>
                <option value="correction">{t('overtime.kinds.correction')}</option>
              </Select>
            </Field>
            <Field label={t('overtime.hours')}>
              <div className="flex gap-2">
                {kind === 'correction' && (
                  <Select className="w-20" value={sign} onChange={(e) => setSign(Number(e.target.value) as 1)} aria-label={t('overtime.sign')}>
                    <option value={1}>+</option>
                    <option value={-1}>-</option>
                  </Select>
                )}
                <HoursInput value={minutes} onChange={setMinutes} />
              </div>
            </Field>
            {kind === 'payout' ? (
              <Field label={t('overtime.month')} hint={t('overtime.payoutHint')}>
                <Input type="month" value={month.slice(0, 7)} onChange={(e) => setMonth(`${e.target.value}-01`)} />
              </Field>
            ) : (
              <Field label={t('common.date')} hint={t('overtime.correctionHint')}>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </Field>
            )}
            <Field label={t('leave.note')} className="sm:col-span-2">
              <Textarea rows={2} value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />
            </Field>
          </div>
          <ErrorBox error={add.error} />
          <div>
            <h3 className="mb-1 text-sm font-semibold">{t('overtime.history')}</h3>
            {q.data ? <AdjustmentList items={q.data.adjustments} onRemove={(a) => setRemoving(a)} /> : <Loading />}
          </div>
        </div>
      </Modal>
      <Modal
        open={!!removing}
        onClose={() => (setRemoving(null), setReason(''))}
        title={t('overtime.removeTitle')}
        footer={
          <>
            <Button onClick={() => (setRemoving(null), setReason(''))}>{t('common.cancel')}</Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 3}
              loading={remove.isPending}
              onClick={() =>
                remove
                  .mutateAsync({ id: removing!.id, reason })
                  .then(() => (setRemoving(null), setReason(''), toast.ok(t('common.saved'))))
                  .catch(toast.error)
              }
            >
              {t('common.delete')}
            </Button>
          </>
        }
      >
        <Field label={t('common.reason')} hint={t('common.reasonHint')}>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </Modal>
    </>
  );
}

